import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';
import { servicesQuery } from '@/services/api';
import { useForm } from '@/components/form';
import { useStore } from '@tanstack/react-form';
import { useSnackbars } from '@customerio/pluma-components/react';
import { formOpts, toAIContextOverrides, toFormData, type FormData } from './helpers/form';
import type { AIContextOverrides, AIContext } from './types';
import { useRef, useCallback } from 'react';

export function useAIContextOverrides() {
  const workspaceId = getWorkspaceIdFromCurrentUrl();

  return servicesQuery.getAIContextOverrides.useQuery({
    queryKey: ['aiContextOverrides', workspaceId],
    queryData: {
      params: { workspaceId: workspaceId ?? 0 },
    },
    enabled: workspaceId != null && workspaceId > 0,
  });
}

export function useUpdateAIContextOverrides() {
  const query = servicesQuery.useQueryClient();
  const workspaceId = getWorkspaceIdFromCurrentUrl();

  const updateMutation = servicesQuery.updateAIContextOverrides.useMutation({
    onSuccess: () => {
      query.invalidateQueries({ queryKey: ['aiContextOverrides', workspaceId] });
    },
  });

  return {
    updateAIContextOverrides: async (overrides: AIContextOverrides) => {
      if (workspaceId == null || workspaceId <= 0) {
        throw new Error('Invalid workspace ID. Cannot update AI context overrides without a valid workspace.');
      }

      await updateMutation.mutateAsync({ params: { workspaceId }, body: { overrides } });
    },
  };
}

export interface ChangeInfo {
  fieldsChangedCount: number;
  fieldsAddedCount: number;
  fieldsEditedCount: number;
  fieldsClearedCount: number;
  changedFields: string[];
}

// Helper to calculate simple field changes for analytics
function calculateFieldChanges(initialData: FormData, currentData: FormData): ChangeInfo {
  const changedFields: string[] = [];
  let added = 0;
  let edited = 0;
  let cleared = 0;

  for (const [key, newVal] of Object.entries(currentData)) {
    const oldVal = initialData[key as keyof FormData];
    const oldStr = String(oldVal ?? '');
    const newStr = String(newVal ?? '');

    if (oldStr !== newStr) {
      changedFields.push(key);
      if (newStr === '') cleared++;
      else if (oldStr === '') added++;
      else edited++;
    }
  }

  return {
    fieldsChangedCount: changedFields.length,
    fieldsAddedCount: added,
    fieldsEditedCount: edited,
    fieldsClearedCount: cleared,
    changedFields,
  };
}

/**
 * Custom hook for managing AI Business Profile form state.
 */
export function useAIBusinessProfileForm(
  aiContext?: AIContext,
  overrides?: AIContextOverrides,
  onSave?: (changeInfo: ChangeInfo & { editSessionMs: number }) => void,
) {
  const { updateAIContextOverrides } = useUpdateAIContextOverrides();
  const snackbars = useSnackbars();
  const editStartTimeRef = useRef<number | null>(null);
  const initialFormDataRef = useRef(toFormData(aiContext, overrides));

  const startEditSession = useCallback(() => {
    editStartTimeRef.current ??= Date.now();
  }, []);

  const form = useForm({
    ...formOpts,
    defaultValues: {
      ...toFormData(aiContext, overrides),
    },
    onSubmit: async ({ value }) => {
      const editSessionMs = editStartTimeRef.current !== null ? Date.now() - editStartTimeRef.current : 0;
      const changeInfo = calculateFieldChanges(initialFormDataRef.current, value);
      await updateAIContextOverrides(toAIContextOverrides(value));
      onSave?.({ ...changeInfo, editSessionMs });

      form.reset(value);
      snackbars.create({
        message: 'AI business profile updated',
      });

      editStartTimeRef.current = null;
      initialFormDataRef.current = value;
    },
  });

  const { isDirty, isValid } = useStore(form.store, (state) => ({ isDirty: state.isDirty, isValid: state.isValid }));

  return {
    form,
    isDirty,
    isValid,
    startEditSession,
  };
}
